/** @jsxImportSource @emotion/react */
import { useMemo } from "react";
import PropTypes from "prop-types";
import CreatableSelect from "react-select/creatable";
import usePatchInsightReview from "hookMutation/usePatchInsightReview";

const hexToRgb = (hex) => {
  if (!hex) return { r: 99, g: 102, b: 241 };
  return {
    r: parseInt(hex.substring(1, 3), 16),
    g: parseInt(hex.substring(3, 5), 16),
    b: parseInt(hex.substring(5, 7), 16),
  };
};

const darkenHex = (hex, factor = 0.75) => {
  const { r, g, b } = hexToRgb(hex);
  const d = (n) =>
    Math.round(n * factor)
      .toString(16)
      .padStart(2, "0");
  return `#${d(r)}${d(g)}${d(b)}`;
};

const customStyles = {
  control: (styles) => ({
    ...styles,
    minHeight: "64px",
    width: "100%",
    border: "none",
    padding: "4px 0",
    boxShadow: "none",
  }),
  valueContainer: (styles) => ({
    ...styles,
    flexWrap: "wrap",
    gap: "4px",
    overflow: "visible",
  }),
  multiValue: (styles, { data }) => {
    const { r, g, b } = hexToRgb(data.color);
    return {
      ...styles,
      backgroundColor: `rgba(${r}, ${g}, ${b}, 0.20)`,
      borderRadius: "6px",
      minWidth: "auto",
      padding: "0 2px",
    };
  },
  multiValueLabel: (styles, { data }) => ({
    ...styles,
    color: darkenHex(data.color || "#6366f1"),
    fontWeight: "600",
    fontSize: "12px",
    padding: "2px 4px",
  }),
  multiValueRemove: (styles, { data }) => ({
    ...styles,
    color: darkenHex(data.color || "#6366f1"),
    opacity: 0.7,
    borderRadius: "4px",
    ":hover": {
      backgroundColor: `rgba(0, 0, 0, 0.08)`,
      color: darkenHex(data.color || "#6366f1"),
      opacity: 1,
    },
  }),
};

export default function TagsCell({ row, getValue, allTags = [] }) {
  const insightId = row.original.documentId;
  const { mutate } = usePatchInsightReview();

  const selectedOptions = useMemo(() => {
    const rawTags = getValue() || [];
    return rawTags
      .map((t) => ({
        label: t.name,
        value: t.id,
        color: t.color,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [getValue]);

  const options = useMemo(() => {
    const selectedValues = new Set(selectedOptions.map((opt) => opt.value));
    return allTags
      .filter((tag) => !selectedValues.has(tag.id))
      .map((t) => ({ label: t.name, value: t.id, color: t.color }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allTags, selectedOptions]);

  const handleChange = (newValue) => {
    // const uniqueIds = newValue ? Array.from(new Set(newValue.map((opt) => opt.value))) : [];
    const tagIds = newValue ? newValue.map((opt) => opt.value) : [];
    mutate({
      id: insightId,
      tags: tagIds,
    });
  };

  return (
    <div style={{ width: "100%", minWidth: "100%" }}>
      <CreatableSelect
        variant="outlined"
        isMulti
        name="tags"
        value={selectedOptions}
        options={options}
        onChange={handleChange}
        placeholder="Add tags..."
        classNamePrefix="react-select"
        styles={customStyles}
        isClearable={false}
      />
    </div>
  );
}

TagsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      documentId: PropTypes.string,
    }),
  }),
  getValue: PropTypes.func.isRequired,
  allTags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  ),
};
